import React from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Input,
  Stack,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/joy';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';

const Toolbar = ({
  breadcrumbs,
  view,
  onViewChange,
  searchQuery,
  onSearchChange,
  selectedCount,
  onClearSelection,
  onBulkDelete,
  onBulkMove,
  onNewFolder,
  onRefresh,
  loading,
  uploadButton,
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={2}
      sx={{
        pt: 0,
        pb: 1.5,
        px: 0,
      }}
    >
      {/* Left side - Breadcrumbs, title, and search */}
      <Stack gap={1} flex={1} minWidth={200}>
        {breadcrumbs && <Box>{breadcrumbs}</Box>}
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 'lg',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.vars.palette.primary[400]} 0%, ${theme.vars.palette.primary[600]} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'sm',
              flexShrink: 0,
            }}
          >
            <FolderOutlinedIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Typography level="h2" component="h1" sx={{ whiteSpace: 'nowrap' }}>
            Storage
          </Typography>
          <Input
            size="sm"
            placeholder="Search files..."
            startDecorator={<SearchIcon sx={{ color: 'neutral.500' }} />}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              minWidth: 220,
              maxWidth: 300,
              flex: 1,
              '--Input-focusedThickness': '1px',
            }}
          />
        </Stack>
      </Stack>

      {/* Center - Bulk Actions (when selected) */}
      {selectedCount > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            bgcolor: 'primary.50',
            borderRadius: 'sm',
            px: 2,
            py: 0.5,
          }}
        >
          <Typography level="body-sm" fontWeight={600} color="primary">
            {selectedCount} selected
          </Typography>
          <Tooltip title="Move selected">
            <IconButton
              size="sm"
              variant="plain"
              color="primary"
              onClick={onBulkMove}
            >
              <DriveFileMoveOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete selected">
            <IconButton
              size="sm"
              variant="plain"
              color="danger"
              onClick={onBulkDelete}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear selection">
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={onClearSelection}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      {/* Right side - View Toggle and Actions */}
      <Stack direction="row" alignItems="center" gap={1}>
        <ToggleButtonGroup
          size="sm"
          value={view}
          onChange={(_, newValue) => newValue && onViewChange(newValue)}
          sx={{
            '--ButtonGroup-separatorColor': 'transparent',
          }}
        >
          <Tooltip title="Grid view">
            <IconButton value="grid" variant={view === 'grid' ? 'solid' : 'outlined'}>
              <GridViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="List view">
            <IconButton value="list" variant={view === 'list' ? 'solid' : 'outlined'}>
              <ViewListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ToggleButtonGroup>

        <Divider orientation="vertical" sx={{ height: 24 }} />

        <Tooltip title="Refresh">
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          startDecorator={<CreateNewFolderOutlinedIcon />}
          onClick={onNewFolder}
        >
          New Folder
        </Button>

        {uploadButton}
      </Stack>
    </Stack>
  );
};

export default Toolbar;
